import { useLazyQuery } from "@apollo/client";
import { useEffect, useState } from "react";
import { getUserQuery } from "../sharedCode/queries";
import { lookupOptions } from "../GQL/lookupOptions";
import { ExtendPerson, PersonExtended } from "../Utils/TypeExtensions";
import { BackgroundProps, SyncStatus, TableSyncer } from "../Utils/Interfaces";
import { TicketDatabase } from "../Utils/TicketDatabase";
import { ObjectUtils } from "../Utils/ObjectUtils";
import { Person } from "../sharedCode/avroGraphql";

export default function useUserBackgroundPull(props: BackgroundProps): TableSyncer {
    const [status, setStatus] = useState(SyncStatus.Loading);
    const [doQuery, { loading: gqlLoading, error: gqlError, data: gqlResult, refetch: refetch }] = useLazyQuery(getUserQuery, lookupOptions);

    const fetchData = (firstRun: boolean) => {
        if (firstRun) {
            doQuery();
        } else {
            setStatus(SyncStatus.Loading);
            props.updateUi();
            refetch()
                .then((results) => {
                    const rawList = [results.data.currentLegacyUser as Person];
                    saveToLocalDatabase(rawList);
                    setStatus(SyncStatus.Fresh);
                    props.updateUi();
                })
                .catch((e) => {
                    setStatus(SyncStatus.Stale);
                    props.updateUi();
                });
        }
    };

    function saveToLocalDatabase(rawList: Person[]) {
        const listToSave = ObjectUtils.convertToUppercase(rawList.map((item: Person) => ExtendPerson(item))) as PersonExtended[];

        TicketDatabase.transaction("rw", TicketDatabase.user, async () => {
            await TicketDatabase.user
                ?.toCollection()
                .primaryKeys()
                .then((keys) => {
                    TicketDatabase.user.bulkDelete(keys);
                })
                .then(() => TicketDatabase.user.bulkAdd(listToSave));
        });
    }

    useEffect(() => {
        if (gqlResult) {
            const rawList = [gqlResult.currentLegacyUser as Person];
            saveToLocalDatabase(rawList);
            setStatus(SyncStatus.Fresh);
            props.updateUi();
        }
    }, [gqlResult]);

    useEffect(() => {
        if (gqlError) {
            TicketDatabase.user.count().then((c) => {
                setStatus(c > 0 ? SyncStatus.Stale : SyncStatus.Error);
                props.updateUi();
            });
        }
    }, [gqlError]);

    useEffect(() => {
        if (gqlLoading) {
            setStatus(SyncStatus.Loading);
            props.updateUi();
        }
    }, [gqlLoading]);

    return { status, fetchData: fetchData };
}
